'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from '@/components/_shared/Modal/Modal';
import { buildReportEventUrl } from './buildReportEventUrl';
import { isTrustedReportEventPostMessageOrigin } from './report-event-post-message';
import styles from './ReportEventIframeModal.module.scss';

export interface ReportEventIframeModalProps {
  visible: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  eventUrl: string;
  country: string;
  locale: string;
  reporterId?: string | null;
  isReporterIdResolved?: boolean;
  reporterEmail?: string | null;
  isReporterEmailResolved?: boolean;
}

const CLOSE_WINDOW_ACTION = 'CLOSE_WINDOW';

export const ReportEventIframeModal: React.FC<ReportEventIframeModalProps> = ({
  visible,
  onClose,
  eventId,
  eventName,
  eventUrl,
  country,
  locale,
  reporterId,
  isReporterIdResolved = true,
  reporterEmail,
  isReporterEmailResolved = true,
}) => {
  const { formatMessage } = useIntl();
  const [isIframeReady, setIsIframeReady] = useState(false);
  const iframeSrcRef = useRef<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setIsIframeReady(false);
      iframeSrcRef.current = null;
      return;
    }

    const frameId = requestAnimationFrame(() => {
      setIsIframeReady(true);
    });

    return () => {
      cancelAnimationFrame(frameId);
      setIsIframeReady(false);
    };
  }, [visible]);

  const iframeSrc = useMemo(() => {
    if (!isIframeReady || !isReporterIdResolved || !isReporterEmailResolved) {
      return null;
    }

    if (!iframeSrcRef.current) {
      iframeSrcRef.current = buildReportEventUrl({
        eventId,
        eventName,
        eventUrl,
        country,
        locale,
        reporterId,
        reporterEmail,
      });
    }

    return iframeSrcRef.current;
  }, [
    isIframeReady,
    isReporterIdResolved,
    isReporterEmailResolved,
    eventId,
    eventName,
    eventUrl,
    country,
    locale,
    reporterId,
    reporterEmail,
  ]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!isTrustedReportEventPostMessageOrigin(event.origin, iframeSrc)) {
        return;
      }

      if (event.data?.action === CLOSE_WINDOW_ACTION) {
        onClose();
      }
    },
    [iframeSrc, onClose],
  );

  useEffect(() => {
    if (!visible) {
      return;
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [visible, handleMessage]);

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      noPadding
      noMinHeight={false}
      __containerClassName={styles.modalContainer}
    >
      <div className={styles.iframeWrapper} data-testid="report-event-modal">
        {iframeSrc ? (
          <iframe
            className={styles.reportEventIframe}
            src={iframeSrc}
            loading="lazy"
            title={formatMessage({
              id: 'Report this event form',
              defaultMessage: 'Report this event form',
              description: 'Accessible title for report event iframe',
            })}
            data-testid="report-event-iframe"
          />
        ) : null}
      </div>
    </Modal>
  );
};
